import { Component, forwardRef, Input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IOption, Option } from './typings/option';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { LucideCheck, LucideChevronsUpDown } from '@lucide/angular';
import { OnClickOutsideDirective } from '@knalgeel/pandora';
import { IconComponent } from '../icon/icon.component';

@Component({
    selector: 'app-select',
    imports: [CommonModule, FormsModule, IconComponent, OnClickOutsideDirective],
    templateUrl: './select.component.html',
    styleUrl: './select.component.scss',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SelectComponent),
            multi: true,
        },
    ],
})
export class SelectComponent implements ControlValueAccessor {

    protected readonly chevronIcon = LucideChevronsUpDown;
    protected readonly checkIcon = LucideCheck;

    _options: IOption[] = [];
    value = signal<IOption['value'] | null>(null);
    open = signal<boolean>(false);

    get options(): IOption[] {
        return this._options;
    }

    @Input()
    set options(options: Option[]) {
        this._options = options.map(option => {
            if (typeof option === 'string') {
                return {
                    label: option,
                    value: option,
                };
            }

            return option;
        });
    }

    get selectedOption(): IOption | null {
        return this.options.find(option => option.value === this.value()) || null;
    }

    toggle() {
        this.open.update(open => !open);
    }

    close() {
        this.open.set(false);
    }

    select(option: IOption) {
        this.value.set(option.value);
        this.onChange(option.value);
        this.open.set(false);
    }

    onChange: (value: IOption['value'] | null) => void = () => {
        // Overwritten by registerOnChange/registerOnTouched; a form control that is
        // never registered still needs something callable here.
    };

    onTouched: () => void = () => {
        // Overwritten by registerOnChange/registerOnTouched; a form control that is
        // never registered still needs something callable here.
    };

    writeValue(value: IOption['value'] | null): void {
        this.value.set(value);
    }

    registerOnChange(fn: (value: IOption['value'] | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

}
